// src/components/managed-sources/CredentialAuthStep.tsx
//
// The wizard's Connect step for every connector whose catalogue item has
// `has_credential_form` (catalog plan 2026-10-13, step 0.5): pick a saved
// connection of the connector's authorization type, or create one with the
// generic CredentialConnectionForm. No per-vendor branch.

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconPlugConnected,
  IconPlus,
  IconX,
} from "@tabler/icons-react";

import { listAuthorizations } from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import { ConnectorIcon } from "@/components/data-sources/ConnectorIcon";
import { CredentialConnectionForm } from "@/components/authorizations/CredentialConnectionForm";
import { useBranding } from "@/context/BrandingContext";

type Props = {
  /** Registry connector type, e.g. "WOOCOMMERCE". */
  connectorType: string;
  /** Customer-facing connector label from the catalogue, e.g. "WooCommerce". */
  label: string;
  /** The catalogue item's `auth_type`: which saved connections to offer. */
  authType: string;
  authorizationId: string | null;
  onAuthorizationChange: (id: string | null) => void;
  onValidated: (valid: boolean) => void;
  /** Reports the selected connection's name, for the review step. */
  onConnectionLabelChange?: (label: string | null) => void;
};

export default function CredentialAuthStep({
  connectorType,
  label,
  authType,
  authorizationId,
  onAuthorizationChange,
  onValidated,
  onConnectionLabelChange,
}: Props) {
  const { branding } = useBranding();
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  async function loadAuths(autoSelectLatest: boolean = false) {
    setLoadingAuths(true);
    setLoadError("");
    try {
      const auths = await listAuthorizations({ type: authType });
      setExistingAuths(auths);
      if (autoSelectLatest && auths.length > 0) {
        const latest = [...auths].sort((a, b) =>
          (b.created_at || "").localeCompare(a.created_at || ""),
        )[0];
        onAuthorizationChange(latest.id);
      }
    } catch (e: any) {
      setLoadError(e?.message || `Failed to load ${label} connections`);
    } finally {
      setLoadingAuths(false);
    }
  }

  useEffect(() => {
    loadAuths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authType]);

  useEffect(() => {
    onValidated(!!authorizationId);
  }, [authorizationId, onValidated]);

  useEffect(() => {
    if (!onConnectionLabelChange) return;
    const selected = existingAuths.find((a) => a.id === authorizationId);
    onConnectionLabelChange(selected?.name ?? null);
  }, [authorizationId, existingAuths, onConnectionLabelChange]);

  const selectOptions = existingAuths.map((a) => ({
    value: a.id,
    label: a.name,
  }));

  return (
    <Stack gap="lg">
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="violet">
          <ConnectorIcon type={connectorType} size={30} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect {label}
          </Text>
          <Text size="sm" c="dimmed">
            Pick the {label} connection {branding.productName} should pull
            data from.
          </Text>
        </Stack>
      </Group>

      {loadingAuths ? (
        <Stack align="center" justify="center" py={40}>
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading {label} connections...
          </Text>
        </Stack>
      ) : loadError ? (
        <Alert color="red" icon={<IconX size={16} />}>
          {loadError}
        </Alert>
      ) : existingAuths.length === 0 || createOpen ? (
        // Inline, in the wizard's own modal: a drawer opened from here would
        // sit under the modal's overlay and focus trap.
        <CredentialConnectionForm
          connectorType={connectorType}
          onCreated={() => {
            setCreateOpen(false);
            loadAuths(true);
          }}
          onCancel={
            existingAuths.length > 0 ? () => setCreateOpen(false) : undefined
          }
        />
      ) : (
        <Stack gap="md">
          <Select
            label={`${label} connection`}
            placeholder="Select a saved connection"
            data={selectOptions}
            value={authorizationId}
            onChange={onAuthorizationChange}
            size="md"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Need a different account?
            </Text>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setCreateOpen(true)}
            >
              Add another {label} connection
            </Button>
          </Group>
        </Stack>
      )}

      {authorizationId && !createOpen && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Connected
              </Text>
              <Text size="xs" c="dimmed">
                {existingAuths.find((a) => a.id === authorizationId)?.name ||
                  `${label} connection ready`}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
